import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import AuthHeroComponent from '../components/AuthHeroComponent';
import LoginComponent from '../components/LoginComponent';
import RegisterComponent from '../components/RegisterComponent';
import ForgotPasswordComponent from '../components/ForgotPasswordComponent';
import AuthClient from '../data/api/AuthClient';
import { ThemeSwitcher } from '../../../shared/components/ThemeSwitcher/ThemeSwitcher';
import '../styles/LoginStyle.css';

const AuthPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isRegister = location.pathname === '/register';
  const isForgot = location.pathname === '/forgot-password';

  useEffect(() => {
    const fetchRegion = async () => {
      try {
        localStorage.clear();
        const response = await AuthClient.getRegion();
        if (response?.region) {
          localStorage.setItem('iqadmin_region', response.region);
        }
      } catch (error) {
        console.error('Failed to fetch region:', error);
      }
    };
    fetchRegion();
  }, []);

  return (
    <div 
      style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        height: '100vh', 
        overflow: 'hidden',
        background: 'linear-gradient(-45deg, var(--bg-subtle), color-mix(in srgb, var(--iq-landing-primary) 15%, white), color-mix(in srgb, var(--iq-gradient-mid) 15%, white), color-mix(in srgb, var(--iq-gradient-end) 15%, white))',
        backgroundSize: '400% 400%',
        animation: 'admin-gradient-bg 15s ease infinite'
      }}
    >
      <style>
        {`
          @keyframes admin-gradient-bg {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
        `}
      </style>
      
      <div className="login-page-container" style={{ height: '100%', width: '100%', position: 'relative' }}>
        
        <AuthHeroComponent variant={isRegister ? 'register' : 'login'} />
        
        <div className="login-form-container" style={{ position: 'relative', overflow: 'hidden' }}>
          
          {/* ═══ SIMPLE TOP LINKS ═══ */}
          <div style={{ position: 'absolute', top: 20, right: 32, display: 'flex', gap: '24px', alignItems: 'center', zIndex: 50 }}>
            <ThemeSwitcher />
            <button 
              onClick={() => navigate('/')} 
              style={{ background: 'transparent', border: 'none', color: 'var(--text-primary, #0f172a)', fontWeight: 600, fontSize: '0.95rem', cursor: 'pointer', opacity: 0.7, transition: 'opacity 0.2s, color 0.2s' }}
              onMouseOver={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.color = 'var(--iq-landing-primary)'; }}
              onMouseOut={e => { e.currentTarget.style.opacity = '0.7'; e.currentTarget.style.color = 'var(--text-primary, #0f172a)'; }}
            >
              Home
            </button>
            <button 
              onClick={() => navigate(isRegister ? '/login' : '/register')} 
              style={{ background: 'var(--iq-landing-primary, #10b981)', color: '#fff', border: 'none', padding: '10px 24px', borderRadius: '100px', fontWeight: 600, fontSize: '0.95rem', cursor: 'pointer', boxShadow: 'var(--iq-landing-shadow, 0 4px 14px rgba(16,185,129,0.3))', transition: 'transform 0.2s, background-color 0.2s, box-shadow 0.2s' }}
              onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.backgroundColor = 'var(--iq-primary, #059669)'; }}
              onMouseOut={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.backgroundColor = 'var(--iq-landing-primary, #10b981)'; }}
            >
              {isRegister ? 'Sign In' : 'Get Started'}
            </button>
          </div>

          <div className="auth-anim-stage">
            <AnimatePresence mode="wait">
              {isForgot ? (
                <motion.div
                  key="forgot-card"
                  className="auth-anim-card"
                  initial={{ rotateY: 90, opacity: 0 }}
                  animate={{ rotateY: 0, opacity: 1 }}
                  exit={{ rotateY: -90, opacity: 0 }}
                  transition={{ duration: 0.4, ease: "easeInOut" }}
                >
                  <ForgotPasswordComponent />
                </motion.div>
              ) : isRegister ? (
                <motion.div
                  key="register-card"
                  className="auth-anim-card"
                  initial={{ rotateY: 90, opacity: 0 }}
                  animate={{ rotateY: 0, opacity: 1 }}
                  exit={{ rotateY: -90, opacity: 0 }}
                  transition={{ duration: 0.4, ease: "easeInOut" }}
                >
                  <RegisterComponent />
                </motion.div>
              ) : (
                <motion.div
                  key="login-card"
                  className="auth-anim-card"
                  initial={{ rotateY: -90, opacity: 0 }}
                  animate={{ rotateY: 0, opacity: 1 }}
                  exit={{ rotateY: 90, opacity: 0 }}
                  transition={{ duration: 0.4, ease: "easeInOut" }}
                >
                  <LoginComponent />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
